import React, { useMemo } from "react";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import PageLayout from "../components/page-layout/PageLayout";
import GridBlock from "../components/grid-block/GridBlock";
import TechIcon from "../components/icons/TechIcon";
import { FeaturesGrid2 } from "../components/features-grid/FeaturesGrid2";
import TextSection from "../components/text/TextSection";
import SideImageTextSection from "../components/text/SideImageTextSection";
import SEO from "../components/SEO";
import { useI18next } from "gatsby-plugin-react-i18next";
import Experts from "../components/sections/Experts";
import { useExperts } from "../hooks/useExperts";
import { useServiceI18nLink } from "../hooks/useI18nLink";
import { useServiceTree } from "../hooks/useServiceTree";
import _ from "lodash";
import SideGridBlock from "../components/grid-block/SideGridBlock";
import Keyword from "../components/typography/Keyword";
import { LinkCta } from "../components/typography/LinkCta";
import ImageCta from "../components/cta/ImageCta";
import { graphql } from "gatsby";
import TextHero from "../components/hero/TextHero";
import { HeroCta } from "../components/cta/HeroCta";

interface UseCase {
  slug: string;
  enSlug: string;
  deSlug: string;
  order: number;
  published: boolean;
  meta: {
    title: { it: string; en: string; de: string };
    description: { it: string; en: string; de: string };
    datePublished: string;
    dateModified: string;
  };
  hero: {
    projectName: { it: string; en: string; de: string };
    services: string[];
    subtitle: { it: string; en: string };
  };
  client: {
    client: string;
    image: string;
    clientDescription: { it: string; en: string };
  };
  challenge: {
    title: { it: string; en: string };
    challenges: { it: string; en: string }[];
    description: { it: string; en: string };
  };
  solution: {
    title: { it: string; en: string };
    solution: { it: string; en: string };
  };
  result: {
    title: { it: string; en: string };
    resultsDescription: { it: string; en: string };
    results: { it: string; en: string }[];
  };
  cta: {
    title: { it: string; en: string };
    description: { it: string; en: string };
  };
  techs: string[];
  experts: string[];
}

interface UseCaseTemplateProps {
  pageContext: { useCase: UseCase };
}

const UseCaseTemplate: FunctionComponent<UseCaseTemplateProps> = ({
  pageContext,
}) => {
  const { useCase } = pageContext;
  const { t } = useTranslation();
  const { language } = useI18next();
  const experts = useExperts(useCase.experts ?? []);
  const { serviceLink } = useServiceI18nLink();
  const _services = useServiceTree();

  const services = useMemo(() => {
    const map = _.keyBy(
      _services.flatMap((service) => service.solutions ?? []),
      (i) => i.id,
    );
    return useCase.hero.services.map((sol) => map[sol]);
  }, [useCase.hero.services, _services]);

  return (
    <PageLayout pageName={useCase.slug} headerBg="gray-900" darkHeader>
      <SEO
        title={useCase.meta.title[language]}
        description={useCase.meta.description[language]}
        datePublished={new Date(useCase.meta.datePublished)}
        dateModified={new Date(useCase.meta.dateModified)}
      />

      <TextHero
        dark
        extraWide
        tag={"use case"}
        title={useCase.hero.projectName[language]}
        description={
          <span
            dangerouslySetInnerHTML={{
              __html: useCase.hero.subtitle[language],
            }}
          />
        }
        cta={
          <HeroCta center className="w-full md:w-auto" methodCta theme="dark" />
        }
      />

      <SideImageTextSection
        tag="client"
        title={useCase.client.client}
        text={
          <p
            className="prose dark:prose-invert"
            dangerouslySetInnerHTML={{
              __html: useCase.client.clientDescription[language],
            }}
          />
        }
        image={
          require(`../assets/images/use-case/${useCase.client.image}`).default
        }
        theme="white"
      />

      <FeaturesGrid2
        theme="white"
        tag={t("use-case:challengeTag")}
        title={useCase.challenge.title[language]}
        description={
          <p
            className="prose dark:prose-invert"
            dangerouslySetInnerHTML={{
              __html: useCase.challenge.description[language],
            }}
          />
        }
        elements={useCase.challenge.challenges.map((i) => i[language])}
      />

      <TextSection
        theme="dark"
        tag={t("use-case:solutionTag")}
        title={useCase.solution.title[language]}
        text={
          <p
            className="prose prose-invert "
            dangerouslySetInnerHTML={{
              __html: useCase.solution.solution[language],
            }}
          />
        }
      />

      <FeaturesGrid2
        theme="gray"
        tag={t("use-case:resultsTag")}
        title={useCase.result.title[language]}
        description={
          <p
            className="prose dark:prose-invert"
            dangerouslySetInnerHTML={{
              __html: useCase.result.resultsDescription[language],
            }}
          />
        }
        elements={useCase.result.results.map((i) => i[language])}
      />

      {/* CTA */}
      <ImageCta
        theme="white"
        tag={t(`use-case:ctaTag`)}
        title={useCase.cta.title[language]}
        description={
          <p
            dangerouslySetInnerHTML={{
              __html: useCase.cta.description[language],
            }}
          />
        }
        image={require("../assets/images/astrorei-cta.jpg").default}
      />

      <GridBlock
        tag={t("use-case:techTag")}
        title={t("use-case:techTitle")}
        theme="gray"
        lg3={useCase.techs.length > 4}
        mobileMultiCol
        blocks={useCase.techs.map((tech) => (
          <div className="flex items-center justify-center p-6">
            <TechIcon
              icon={tech}
              size="xl"
              caption={tech}
              className="text-gray-800 dark:text-gray-200 z-10"
            />
          </div>
        ))}
      />

      {/* services */}
      <SideGridBlock
        tag={t(`use-case:serviceCta.tag`)}
        title={t(`use-case:serviceCta.title`)}
        description={t(`use-case:serviceCta.description`)}
        theme="white"
        singleColumn
        blocks={services.map((solution) => (
          <>
            <Keyword title={t(`solutions:${solution.id}.name`)} />
            <LinkCta
              to={serviceLink(solution)}
              text={t("index:customSoftwareSolutions.ctaLink")}
              align="right"
            />
          </>
        ))}
      />

      {/* EXPERTS */}
      {experts && experts.length > 0 && (
        <Experts
          tag={t("solutions:team.tag")}
          title={t("solutions:team.title")}
          experts={experts}
        />
      )}
    </PageLayout>
  );
};

export default UseCaseTemplate;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
